import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import { CaptureBox } from "./capture-box";

/** Alt+N from anywhere in a story: jot an idea into the inbox without leaving what you're doing. */
export function QuickCaptureDialog({
  storyId,
  open,
  onOpenChange,
}: {
  storyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [saved, setSaved] = useState(0);

  useEffect(() => {
    if (open) setSaved(0);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Capture an idea</DialogTitle>
          <DialogDescription>
            It waits with your other ideas until it has a place. Keep typing to add more; Esc when you're done.
          </DialogDescription>
        </DialogHeader>
        <CaptureBox
          storyId={storyId}
          autoFocus
          onSaved={(count) => {
            setSaved((n) => n + count);
            toast.success(count === 1 ? "Saved to your ideas" : `Saved ${count} ideas`);
          }}
        />
        {saved > 0 && (
          <p className="text-xs text-muted-foreground">
            {saved} saved this time.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Alt+N opens quick capture. Uses the key code so Option+N works on macOS. */
export function useQuickCaptureShortcut(onOpen: () => void) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.altKey && !event.ctrlKey && !event.metaKey && event.code === "KeyN") {
        event.preventDefault();
        onOpen();
      }
    };
    document.addEventListener("keydown", handler, { capture: true });
    return () => document.removeEventListener("keydown", handler, { capture: true });
  }, [onOpen]);
}
